import { prepareImageForUpload } from "@ths/shared/browser";
import {
  ISSUE_PHOTO_MAX_BYTES,
  ISSUE_PHOTO_MAX_COUNT,
  ISSUE_PHOTO_MAX_DIMENSION,
} from "@ths/shared/utils";
import { Button } from "@ths/ui";
import { useEffect, useRef, useState } from "react";

export interface SelectedPhoto {
  id: string;
  file: File;
  previewUrl: string;
}

interface Props {
  photos: SelectedPhoto[];
  onChange: (next: SelectedPhoto[]) => void;
  disabled?: boolean;
  onProcessingChange: (processing: boolean) => void;
}

export default function PhotoUploader({
  photos,
  onChange,
  disabled = false,
  onProcessingChange,
}: Props) {
  const maxPhotos = ISSUE_PHOTO_MAX_COUNT;
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);
  const mountedRef = useRef(true);
  const previewsRef = useRef<SelectedPhoto[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    for (const photo of previewsRef.current) {
      if (!photos.some((next) => next.id === photo.id))
        URL.revokeObjectURL(photo.previewUrl);
    }
    previewsRef.current = photos;
  }, [photos]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const photo of previewsRef.current)
        URL.revokeObjectURL(photo.previewUrl);
    };
  }, []);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length || processingRef.current || disabled) return;
    const selected = Array.from(files);
    const available = maxPhotos - photos.length;
    const errors =
      selected.length > available ? [`Max ${maxPhotos} photos.`] : [];
    processingRef.current = true;
    setProcessing(true);
    setError(null);
    onProcessingChange(true);
    if (inputRef.current) inputRef.current.value = "";
    const newPhotos: SelectedPhoto[] = [];
    try {
      for (const original of selected.slice(0, available)) {
        try {
          const file = await prepareImageForUpload(original, {
            maxDimension: ISSUE_PHOTO_MAX_DIMENSION,
            maxBytes: ISSUE_PHOTO_MAX_BYTES,
          });
          if (!mountedRef.current) break;
          newPhotos.push({
            id: crypto.randomUUID(),
            file,
            previewUrl: URL.createObjectURL(file),
          });
        } catch (cause) {
          errors.push(
            cause instanceof Error
              ? cause.message
              : "This photo could not be processed. Try another image.",
          );
        }
      }
      if (mountedRef.current) {
        onChange([...photos, ...newPhotos]);
        setError(errors.length ? [...new Set(errors)].join(" ") : null);
      } else {
        for (const photo of newPhotos) URL.revokeObjectURL(photo.previewUrl);
      }
    } finally {
      processingRef.current = false;
      if (mountedRef.current) {
        setProcessing(false);
        onProcessingChange(false);
      }
    }
  };

  const atMax = photos.length >= maxPhotos;

  return (
    <div>
      <label
        htmlFor="bc-photo-input"
        className="mb-2 block text-label text-text"
      >
        Photos (optional, up to {maxPhotos})
      </label>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        disabled={disabled || processing || atMax}
        onChange={(e) => void handleFiles(e.target.files)}
        className="hidden"
        id="bc-photo-input"
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={atMax || disabled || processing}
        variant="neutral"
        fullWidth
      >
        <span aria-hidden="true">+</span>
        <span>
          {processing
            ? "Preparing photos…"
            : atMax
              ? "Maximum reached"
              : "Add photo"}
        </span>
      </Button>
      {error && (
        <div role="alert" className="mt-2 text-body text-error-text">
          {error}
        </div>
      )}
      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <div
              key={p.id}
              className="relative overflow-hidden rounded-md border border-separator"
            >
              <img
                src={p.previewUrl}
                alt=""
                className="h-20 w-full object-cover"
              />
              <Button
                type="button"
                disabled={disabled || processing}
                onClick={() => onChange(photos.filter((x) => x.id !== p.id))}
                variant="neutral"
                size="sm"
                className="absolute right-1 top-1 size-8 p-0"
                aria-label="remove photo"
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
